/**
 * Extrae el mensaje de error de una respuesta de la API para mostrarlo al usuario.
 *
 * Nunca usa `err.message` de axios: es texto crudo en inglés
 * ("Request failed with status code 500", "Network Error") y se filtra a la UI.
 * Si el servidor no envía un mensaje útil, se usa el `fallback` en es-DO.
 */

/**
 * c730 — envoltorios de error DEL SERVIDOR, en inglés.
 *
 * El `errorHandler` global, el middleware de validación, la configuración de
 * auth y el limitador global responden con texto pensado para developers. Al
 * pasarlo tal cual a la UI el usuario lee inglés dentro de una app es-DO, justo
 * debajo de avisos en español (p. ej. `BootStatusBanner`). Ninguna de estas
 * cadenas dice QUÉ hacer, así que se sustituye por texto accionable en es-DO.
 *
 * Enumerado contra el servidor, no adivinado: `middleware/errorHandler.js`,
 * `middleware/validation.js`, `routes/auth.js` y el limitador de `server.js`.
 * `ApiErrorSpanishRatchet.spec.ts` falla si el servidor añade un envoltorio
 * nuevo que aquí no esté.
 */
const GENERIC_ENVELOPE_MESSAGES = new Set([
  // middleware/errorHandler.js — 500
  'An unexpected error occurred',
  // En producción el 5xx se lava antes de salir; esto es lo que llega al cliente.
  'An internal error occurred',
  'Internal Server Error',
  // middleware/errorHandler.js — 400/409 de Sequelize
  'The provided data is invalid',
  'Validation Error',
  'A record with this information already exists',
  'Duplicate Entry',
  'The referenced record does not exist',
  'Reference Error',
  // middleware/errorHandler.js — 503
  'Database connection failed',
  'Service Unavailable',
  // middleware/errorHandler.js — 401/403
  'Invalid or expired token',
  'Authentication Error',
  'Token has expired',
  'Cross-origin request not allowed',
  'CORS Error',
  // middleware/validation.js — 400 (Joi / express-validator)
  'The request contains invalid data',
  'Validation failed',
  'An error occurred during request validation',
  'Validation system error',
  // routes/auth.js — 503
  'Server misconfiguration',
  'JWT_SECRET environment variable is not set',
  // server.js — limitador global. NO trae `code`: sólo `error` + `retryAfter`.
  'Too many requests from this IP, please try again later.'
])

/**
 * c730 — texto es-DO por `code`.
 *
 * El `code` es la etiqueta estable de la infraestructura. El status SOLO no
 * alcanza (el 429 llega sin `code`). Y el `code` solo tampoco: una ruta que
 * responde 500 con `code:'INTERNAL_SERVER_ERROR'` trae además un `message`
 * es-DO con información del negocio ('Error al obtener las cuentas bancarias'),
 * que es MÁS útil que el texto genérico del mapa — por eso `message` propio se
 * consulta antes, y la tabla sólo cubre el hueco cuando no hay nada específico.
 */
const CODE_MESSAGES: Record<string, string> = {
  INTERNAL_SERVER_ERROR: 'Ocurrió un error inesperado. Inténtalo de nuevo en unos momentos.',
  DATABASE_CONNECTION_ERROR: 'No pudimos conectar con la base de datos. Inténtalo en unos segundos.',
  VALIDATION_SYSTEM_ERROR: 'No pudimos validar los datos del formulario. Inténtalo de nuevo.',
  CONFIG_ERROR: 'Este servicio no está disponible en este momento. Inténtalo más tarde.',
  CORS_NOT_ALLOWED: 'No pudimos completar la operación por seguridad. Recarga la página e inténtalo de nuevo.',
  INVALID_TOKEN: 'Tu sesión no es válida. Inicia sesión de nuevo.',
  TOKEN_EXPIRED: 'Tu sesión expiró. Inicia sesión de nuevo.',
  DUPLICATE_ENTRY: 'Ya existe un registro con esta información.',
  FOREIGN_KEY_CONSTRAINT: 'El registro relacionado no existe o fue eliminado.'
}

/** c730 — el limitador no manda `code`, así que su status se atiende aparte. */
const RATE_LIMIT_MESSAGE = 'Demasiadas solicitudes. Espera un momento antes de volver a intentarlo.'

/** c730 — último recurso para un 5xx cuyo texto del servidor ya se descartó. */
const SERVER_ERROR_MESSAGE =
  'No pudimos completar la operación por un problema del servidor. Inténtalo en unos momentos.'

/**
 * c730 — `details` llega en DOS formas y sólo se leía una.
 *
 * Sequelize manda un array de `{ message }`. `middleware/validation.js` manda
 * un OBJETO `{ errors, errorCount, validationResults }`, y `express-validator`
 * nombra el campo `msg`. Con el `Array.isArray` de antes el 400 de validación
 * nunca matcheaba: los mensajes en es-DO de `.withMessage()` se perdían y
 * ganaba el inglés `'The request contains invalid data'`.
 */
function extractDetailMessages (details: unknown): string[] {
  const asArray = Array.isArray(details)
    ? details
    : Array.isArray((details as { errors?: unknown } | null)?.errors)
      ? (details as { errors: unknown[] }).errors
      : []

  return asArray
    .map((entry: unknown) => {
      if (typeof entry === 'string') return entry.trim()
      const item = entry as { message?: unknown; msg?: unknown } | null
      const text = typeof item?.message === 'string'
        ? item.message
        : typeof item?.msg === 'string' ? item.msg : ''
      return text.trim()
    })
    .filter(Boolean)
}

/** ¿El texto del servidor es uno de SUS envoltorios genéricos en inglés? */
function isServerBoilerplate (text: unknown): boolean {
  return typeof text === 'string' && GENERIC_ENVELOPE_MESSAGES.has(text.trim())
}

/**
 * c732 — el `message` de RUTA tampoco siempre viene en es-DO.
 *
 * `CODE_MESSAGES` se consulta POR DEBAJO del `message` del servidor a
 * propósito: una ruta que responde bien escribe su motivo en es-DO y es más
 * específico que el texto del mapa. Ese supuesto no se cumple en
 * `routes/users.js`, donde las cuatro razones de rechazo son texto pensado
 * para developers. Su `code` es estable, pero el `message` inglés gana la
 * precedencia y se muestra crudo — verificado en vivo: crear un usuario con un
 * correo repetido respondía "A user with this email address already exists"
 * dentro de una app es-DO.
 *
 * Se traduce la CADENA, no se descarta: descartarla tiraría también el motivo
 * concreto y el usuario volvería al "Error al crear el usuario" genérico, que
 * no le dice qué corregir.
 *
 * Enumerado contra el servidor, no adivinado. Una clave por cada `message`
 * distinto de las rutas que hoy responden en inglés; `routes/users.js` es el
 * único archivo cubierto hoy. `apiErrorMessage.routeMessages.spec.ts` reproduce
 * la forma exacta de cada rechazo y falla si aparece una razón nueva sin
 * traducir.
 */
const ENGLISH_ROUTE_MESSAGES = new Map<string, string>([
  // routes/users.js — 404, ×4 (GET por id, PUT, DELETE, restore)
  ['User not found in this tenant', 'Ese usuario ya no existe.'],
  // routes/users.js — 400
  ['You cannot delete your own account', 'No puedes eliminar tu propia cuenta.'],
  // routes/users.js — 400
  ['User is not currently deleted', 'Ese usuario no está eliminado.'],
  // routes/users.js — 409
  [
    'A user with this email address already exists',
    'Ya existe un usuario con este correo electrónico.'
  ]
])

/** Texto propio del servidor que SÍ vale la pena mostrar (es-DO de ruta). */
function serverText (text: unknown): string | null {
  if (typeof text !== 'string') return null
  const trimmed = text.trim()
  return trimmed.length > 0 && !isServerBoilerplate(trimmed) ? trimmed : null
}

/**
 * c731 — `Error` que CONSERVA la respuesta de la que salió.
 *
 * La familia BYPASS (68 sitios en 14 archivos) hacía:
 *
 *   throw new Error(response.data.message || 'Error al cargar los usuarios')
 *
 * Eso ocurre en el `else` de `if (response.data.success)`, o sea HTTP **2xx con
 * `success:false`**: no hay rejection de axios, así que no hay nada que
 * re-lanzar. Fabricar un `Error` plano era la única opción obvia — y destruye
 * TRES cosas a la vez:
 *
 *   1. `.response` desaparece ⇒ `getApiErrorMessage()`, que sólo mira
 *      `err.response.status` / `.data`, cae siempre al `fallback`. El `code`,
 *      el `details` y el texto es-DO de la ruta quedan **estructuralmente
 *      inalcanzables**: el usuario ve "Error al crear el usuario" en vez de
 *      "Ese usuario ya existe" o el campo que falló.
 *   2. `.message` queda congelado en el texto CRUDO del servidor, que es
 *      boilerplate en inglés la mitad de las veces — y las vistas que hacen
 *      `err instanceof Error ? err.message : …` lo pintan literal.
 *   3. Se pierde `status`, así que un 429 o un 5xx tampoco se distinguen.
 *
 * Este constructor arregla las tres: deja `response` puesta (para que
 * `getApiErrorMessage` siga teniendo todo lo que necesita si el error sube más
 * allá de este store) y **traduce `.message` de antemano**, de modo que
 * cualquier consumidor —`getApiErrorMessage` o un `toast.error(e.message)`
 * directo— reciba es-DO por el camino corto.
 *
 * Se pasa la `response` de axios, no `response.data`: el status vive ahí.
 */
export function apiErrorFromResponse<T = unknown> (
  response: { status?: number; data?: T } | null | undefined,
  fallback: string
): Error & { response?: { status?: number; data?: T } } {
  const status = typeof response?.status === 'number' ? response.status : undefined

  // Un `response` sin status no sirve ni para traducir ni para conservarse:
  // se devuelve el error plano para no fingir una estructura que no existe.
  if (status === undefined) {
    return new Error(fallback)
  }

  const carried = { status, data: response?.data }

  const error = new Error(getApiErrorMessage({ response: carried }, fallback)) as Error & {
    response?: { status?: number; data?: T }
  }
  error.response = carried
  return error
}

export function getApiErrorMessage (err: unknown, fallback: string): string {
  const response = (err as { response?: { status?: unknown; data?: unknown } } | null)?.response
  const status = typeof response?.status === 'number' ? response.status : null
  const data = response?.data as
    | { code?: unknown; message?: unknown; error?: unknown; details?: unknown }
    | null
    | undefined

  if (data) {
    if (data.code === 'TENANT_ID_MISSING') {
      return 'No pudimos validar tu sesión. Inicia sesión de nuevo.'
    }

    // c730: el limitador es el único envoltorio sin `code`. Se atiende por status.
    if (status === 429) return RATE_LIMIT_MESSAGE

    const detailMessages = extractDetailMessages(data.details)
    const serverMessage = serverText(data.message)
    const serverError = serverText(data.error)

    // c730: los detalles son lo ÚTIL (es-DO, campo por campo). Ganarle al texto
    // genérico del servidor, pero NO pisar un mensaje de ruta que sí informa.
    if (detailMessages.length && !serverMessage) return detailMessages.join(' · ')

    // c730: `message` propio del servidor ya viene en es-DO y es lo más
    // específico que hay — gana por encima del texto genérico del mapa.
    // c732: si esa ruta concreta responde en inglés, se traduce la cadena; se
    // conserva el motivo en vez de caer al `fallback` genérico del sitio.
    if (serverMessage) return ENGLISH_ROUTE_MESSAGES.get(serverMessage) ?? serverMessage

    // c730: `code` es la etiqueta estable de la infraestructura.
    const codeMessage = typeof data.code === 'string' ? CODE_MESSAGES[data.code] : undefined
    if (codeMessage) return codeMessage

    // Un 5xx jamás debe caer a `error`: ahí el servidor pone `error.message`
    // crudo (código interno) y el `message` es lo que informa.
    if (serverError && !(status !== null && status >= 500)) return serverError

    // c730: 5xx con texto del servidor descartado por ser boilerplate, o sin
    // texto (producción lava el `message` de los 5xx antes de salir).
    if (status !== null && status >= 500) return SERVER_ERROR_MESSAGE

    if (detailMessages.length) return detailMessages.join(' · ')
  }

  return fallback
}